import {SiteWideUnlockForm, siteWideUnlockPayload} from '../ipop/site-wide-unlock/site-wide-unlock-settings';
import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, ParamMap, Params, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {MenuItem, MessageService} from 'primeng/api';
import {NavigationService} from '../../../../service/navigation.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {AccountService} from '../../../../service/account.service';
import {AccountInterface} from '../../../../model/account.interface';
import {IpopConfigurationService} from '../../../../service/ipop-configuration.service';
import {IpopConfiguration} from '../../../../model/ipop-configuration.model';
import {OfferRegistryProcessorSyncService} from '../../../../service/offer-registry-processor-sync.service';
import {OfferRegistryProcessStatusInterface} from '../../../../model/offer-registry-process-status.interface';
import {combineLatest, finalize, repeat, switchMap, Subject, takeUntil} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {OfferRuleService} from '../../../../service/offer-rule.service';
import {
  BulkOfferRuleData,
  BulkOfferRuleRequest,
  BulkOfferRuleResponse
} from '../../../../model/bulk-offer-rule.model';
import {OfferRuleChangeSet} from '../../../../model/explicit-save-offer-rule.model';
import {
  IpopExplicitSaveOfferSetRulesComponent
} from './offer-set-rules/ipop-explicit-save-offer-set-rules.component';
import {GeneratedOfferRuleHandoffService} from '../../../../service/generated-offer-rule-handoff.service';
import {OfferRuleModel} from '../../../../model/offer-rule.model';

const processStatusPollingIntervalMs = 3000;
const duplicateQueueStatusCode = 409;
const rulesProcessingToastDetail = 'The rules are being processed, offer data will be processed and synced within the next 10 minutes.';
const processStatusLoadErrorDetail = 'Unable to retrieve offer registry process status';
const processQueueErrorDetail = 'Unable to queue rule processing';
const unsavedRuleNavigationConfirmationMessage = 'You have unsaved rule changes. Leave this page and discard them?';
const accountRouteRestoreErrorDetail = 'Unable to keep the current account open. Please try again.';
const maximumBulkOperations = 50;
const ruleSaveErrorDetail = 'Unable to save offer rules';
const savedButNotQueuedDetail = 'Rules were saved, but processing could not be queued. Use Process Rules to try again.';
const generatedRuleHandoffWarningDetail = 'The generated rule could not be opened. You can still create a rule manually.';
const offerSetRulesTab = 'offerSetRules';
const vdpOffersTab = 'vdpOffers';
const privateOffersTab = 'privateOffers';
const tabQueryParameter = 'tab';
const ruleDraftTokenQueryParameter = 'ruleDraftToken';
const accountIdPattern = /^[1-9]\d*$/;

@Component({
  selector: 'auto-ipop-explicit-save-configuration',
  templateUrl: './ipop-explicit-save-configuration.component.html',
  styleUrls: ['./ipop-explicit-save-configuration.component.scss'],
  standalone: false
})
export class IpopExplicitSaveConfigurationComponent implements OnInit, OnDestroy {
  @ViewChild(IpopExplicitSaveOfferSetRulesComponent)
  private offerSetRulesComponent: IpopExplicitSaveOfferSetRulesComponent;
  accountId: number;
  settingsSideNav: MenuItem[];
  account: AccountInterface;
  privateOffersForm: UntypedFormGroup;
  vdpOffersForm: UntypedFormGroup;
  siteWideUnlockForm: SiteWideUnlockForm | null = null;
  ipopConfiguration: IpopConfiguration | null = null;
  isSaving = false;
  isSavingRules = false;
  isProcessingRules = false;
  isProcessStatusInitialized = false;
  isOfferRegistryProcessingActive = false;
  isRuleConfigurationDirty = false;
  hasUnsavedRuleChangesState = false;
  pendingGeneratedRuleDraft: OfferRuleModel | null = null;
  activeTab = 'privateOffers';
  processStatus: OfferRegistryProcessStatusInterface | null = null;
  private isProcessStatusLoadFailed = false;
  private isComponentDestroyed = false;
  private processStatusPollingSubject: Subject<void> = new Subject<void>();
  private settingsLoadCancellationSubject: Subject<void> = new Subject<void>();
  private destroySubject: Subject<void> = new Subject<void>();
  private ruleSaveAttemptSequence = 0;
  private activeRuleSaveAttemptId: number | null = null;
  private pendingRuleDraftToken: string | null = null;
  private generatedRuleHandoffAttemptKey: string | null = null;
  private warnedGeneratedRuleHandoffAttemptKey: string | null = null;
  private acceptedQueryParameters: Params = {};

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    protected formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    protected messageService: MessageService,
    protected accountService: AccountService,
    private ipopConfigurationService: IpopConfigurationService,
    private offerRegistryProcessorSyncService: OfferRegistryProcessorSyncService,
    private offerRuleService: OfferRuleService,
    private generatedOfferRuleHandoffService: GeneratedOfferRuleHandoffService
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('IPOP');
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntil(this.destroySubject))
      .subscribe(([params, queryParams]) => {
        this.handleRouteParameters(params, queryParams);
      });
  }

  /**
   * Applies route account and tab parameters after protecting unsaved rule changes.
   */
  private handleRouteParameters(params: ParamMap, queryParams: ParamMap): void
  {
    const routeAccountId = this.parseRouteAccountId(params.get('accountId'));
    const requestedTab = queryParams.get(tabQueryParameter);
    const hasDraftToken = queryParams.has(ruleDraftTokenQueryParameter);
    const accountChanged = routeAccountId !== this.accountId;
    const shouldApplyRoute = this.confirmReusedRouteAccountChange(routeAccountId, accountChanged);

    if (shouldApplyRoute) {
      if (accountChanged) {
        this.prepareForRuleSaveAccountChange();
      }
      this.accountId = routeAccountId ?? 0;
      this.acceptedQueryParameters = this.copyQueryParameters(queryParams);
      this.pendingRuleDraftToken = null;
      if (hasDraftToken) {
        this.pendingRuleDraftToken = queryParams.get(ruleDraftTokenQueryParameter) ?? '';
      }
      this.applyRequestedTab(requestedTab);
      this.initializeRouteAccount(accountChanged);
      this.consumePendingGeneratedRuleDraft();
    }
  }

  /**
   * Confirms a reused-route account change before replacing local rule state.
   */
  private confirmReusedRouteAccountChange(routeAccountId: number | null, accountChanged: boolean): boolean
  {
    let shouldApplyRoute = true;
    const hasCurrentAccount = Number.isInteger(this.accountId) && this.accountId > 0;

    if (accountChanged && hasCurrentAccount && (this.hasUnsavedRuleChanges() || this.hasUnsavedSiteWideUnlockSettings())) {
      let confirmationMessage = unsavedRuleNavigationConfirmationMessage;
      if (this.hasUnsavedSiteWideUnlockSettings()) {
        confirmationMessage = 'You have unsaved settings. Leave this account and discard them?';
      }
      shouldApplyRoute = window.confirm(confirmationMessage);
      if (!shouldApplyRoute) {
        this.restoreAcceptedAccountRoute();
      }
    }

    return shouldApplyRoute;
  }

  /**
   * Creates a detached copy of the accepted query parameters.
   */
  private copyQueryParameters(queryParams: ParamMap): Params
  {
    const copiedParameters: Params = {};
    for (const key of queryParams.keys) {
      const values = queryParams.getAll(key);
      let copiedValue: string | string[] = values;
      if (values.length === 1) {
        copiedValue = values[0] ?? '';
      }
      copiedParameters[key] = copiedValue;
    }

    return copiedParameters;
  }

  /**
   * Restores the last accepted account route after a canceled account change.
   */
  private restoreAcceptedAccountRoute(): void
  {
    this.router.navigate(
      ['/settings/ipop-explicit-save', this.accountId],
      {queryParams: this.acceptedQueryParameters, replaceUrl: true}
    ).then((wasRestored) => {
      if (!wasRestored) {
        this.showAccountRouteRestoreError();
      }
    }, () => {
      this.showAccountRouteRestoreError();
    });
  }

  /**
   * Notifies the user when the accepted account route cannot be restored.
   */
  private showAccountRouteRestoreError(): void
  {
    this.messageService.add({
      severity: 'error',
      summary: 'Navigation error',
      detail: accountRouteRestoreErrorDetail
    });
  }

  /**
   * Parses a route value into a safe positive account identifier.
   */
  private parseRouteAccountId(value: string | null): number | null {
    let accountId: number | null = null;
    if (value !== null && accountIdPattern.test(value)) {
      const parsedAccountId = Number(value);
      if (Number.isSafeInteger(parsedAccountId)) {
        accountId = parsedAccountId;
      }
    }

    return accountId;
  }

  /**
   * Selects a supported tab and defaults invalid requests to Private Offers.
   */
  private applyRequestedTab(requestedTab: string | null): void {
    this.activeTab = privateOffersTab;
    if (requestedTab === offerSetRulesTab) {
      this.activeTab = offerSetRulesTab;
    }
    if (requestedTab === 'siteWideUnlock') {
      this.activeTab = 'privateOffers';
        if (this.ipopConfiguration === null || this.ipopConfiguration.canConfigureSiteWideUnlock) {
          this.activeTab = 'siteWideUnlock';
        }
    }
    if (requestedTab === vdpOffersTab) {
      this.activeTab = vdpOffersTab;
    }
  }

  /**
   * Loads account-scoped state or handles an invalid route account.
   */
  private initializeRouteAccount(accountChanged: boolean): void {
    if (this.isValidAccountId() && accountChanged) {
      this.initNavigation();
      this.loadSettings();
      this.loadProcessStatus();
    }
    if (!this.isValidAccountId()) {
      this.handleInvalidAccountId();
    }
  }

  /**
   * Clears the pending generated rule after the child handles it.
   */
  onGeneratedRuleDraftHandled(): void {
    this.pendingGeneratedRuleDraft = null;
  }

  /**
   * Consumes an account-bound generated rule handoff once.
   */
  private consumePendingGeneratedRuleDraft(): void {
    const token = this.pendingRuleDraftToken;
    let attemptKey: string | null = null;

    if (token !== null && this.isValidAccountId()) {
      attemptKey = `${this.accountId}:${token}`;
    }

    if (attemptKey && attemptKey !== this.generatedRuleHandoffAttemptKey) {
      this.generatedRuleHandoffAttemptKey = attemptKey;
      const result = this.generatedOfferRuleHandoffService.consume(token as string, this.accountId);
      if (result.status === 'success') {
        this.pendingGeneratedRuleDraft = this.cloneGeneratedRule(result.rule);
      } else {
        this.showGeneratedRuleHandoffWarning(attemptKey);
      }
      this.removeGeneratedRuleTokenFromUrl(attemptKey);
    }
  }

  /**
   * Copies a generated rule before passing it to the rule editor.
   */
  private cloneGeneratedRule(rule: OfferRuleModel): OfferRuleModel {
    const copiedRule: OfferRuleModel = {
      accountId: rule.accountId,
      offerSetId: rule.offerSetId,
      year: rule.year,
      make: rule.make,
      model: rule.model,
      trim: rule.trim,
      maxSalePrice: rule.maxSalePrice,
      vehicleLimit: rule.vehicleLimit,
      vehicleCondition: rule.vehicleCondition,
      dollarAmountDiscount: rule.dollarAmountDiscount,
      isPrivate: rule.isPrivate,
      status: rule.status
    };

    return copiedRule;
  }

  /**
   * Shows one warning for an unavailable generated rule handoff attempt.
   */
  private showGeneratedRuleHandoffWarning(attemptKey: string): void {
    if (this.warnedGeneratedRuleHandoffAttemptKey !== attemptKey) {
      this.warnedGeneratedRuleHandoffAttemptKey = attemptKey;
      this.messageService.add({
        severity: 'warn',
        summary: 'Generated rule unavailable',
        detail: generatedRuleHandoffWarningDetail
      });
    }
  }

  /**
   * Removes the consumed draft token from the current URL.
   */
  private removeGeneratedRuleTokenFromUrl(attemptKey: string): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {[ruleDraftTokenQueryParameter]: null},
      queryParamsHandling: 'merge',
      replaceUrl: true
    }).catch(() => {
      this.showGeneratedRuleHandoffWarning(attemptKey);
    });
  }

  /**
   * Cleanup component subscriptions.
   */
  ngOnDestroy(): void
  {
    this.isComponentDestroyed = true;
    this.invalidateRuleSaveAttempt();
    this.cancelProcessStatusPolling();
    this.processStatusPollingSubject.complete();
    this.settingsLoadCancellationSubject.next();
    this.ipopConfiguration = null;
    this.siteWideUnlockForm = null;
    this.settingsLoadCancellationSubject.complete();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  /**
   * Initialize the side navigation menu
   */
  initNavigation(): void {
    this.navigationService
      .getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => (this.settingsSideNav = navItems));
  }

  /**
   * Load account and IPOP configurations
   */
  private loadSettings(): void {
    const accountId = this.accountId;
    this.settingsLoadCancellationSubject.next();
    this.ipopConfiguration = null;
    this.siteWideUnlockForm = null;

    this.accountService.getAccount(accountId)
      .pipe(
        takeUntil(this.settingsLoadCancellationSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (account) => {
          this.account = account;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load account'
          });
        }
      });

    this.ipopConfigurationService.getSettings(accountId)
      .pipe(
        takeUntil(this.settingsLoadCancellationSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (settings) => {
          this.applySettingsResponse(settings);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load settings'
          });
        }
      });
  }

  /** Leaves the Sitewide tab when permission or the release flag is unavailable. */
  private applySettingsResponse(settings: IpopConfiguration): void
  {
    this.ipopConfiguration = settings;
    if (this.activeTab === 'siteWideUnlock' && !settings.canConfigureSiteWideUnlock) {
      this.activeTab = 'privateOffers';
      this.siteWideUnlockForm = null;
    }
  }

  onSiteWideUnlockFormGroupInitialized(form: SiteWideUnlockForm): void
  {
    this.siteWideUnlockForm = form;
  }

  hasUnsavedSiteWideUnlockSettings(): boolean {
    return this.siteWideUnlockForm?.dirty === true;
  }

  /**
   * Initialize the private offers form group
   */
  onPrivateOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.privateOffersForm = form;
  }

  /**
   * Initialize the VDP offers form group
   */
  onVdpOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.vdpOffersForm = form;
  }

  /**
   * Check if the save button should be disabled
   */
  getSaveButtonDisabled(): boolean
  {
    let isDisabled = !(this.siteWideUnlockForm?.dirty && this.siteWideUnlockForm.valid);

    if (this.privateOffersForm && this.privateOffersForm.dirty && this.privateOffersForm.valid) {
      isDisabled = false;
    }
    if (this.vdpOffersForm && this.vdpOffersForm.dirty && this.vdpOffersForm.valid) {
      isDisabled = false;
    }
    if (this.isOfferRegistryProcessingActive || this.isProcessingRules || this.isProcessStatusLoadFailed) {
      isDisabled = true;
    }

    if (!this.ipopConfiguration || this.ipopConfiguration.accountId !== this.accountId) {
      isDisabled = true;
    }
    if (this.isSaving || (this.siteWideUnlockForm?.dirty && !this.siteWideUnlockForm.valid)) {
      isDisabled = true;
    }

    return isDisabled;
  }

  /**
   * Checks whether the Process Rules button should be disabled.
   */
  getProcessRulesButtonDisabled(): boolean
  {
    const isDisabled = this.getRuleActionsDisabled() || this.hasUnsavedRuleChanges();

    return isDisabled;
  }

  /**
   * Checks whether rule actions are locked by status or active operations.
   */
  getRuleActionsDisabled(): boolean
  {
    const isDisabled = this.isProcessStatusUnavailable()
      || this.isRuleOperationInProgress()
      || !this.isValidAccountId();

    return isDisabled;
  }

  /**
   * Checks whether process status is unresolved or failed.
   */
  private isProcessStatusUnavailable(): boolean
  {
    const isUnavailable = !this.isProcessStatusInitialized || this.isProcessStatusLoadFailed;

    return isUnavailable;
  }

  /**
   * Checks whether processing, queueing, or saving currently locks rule actions.
   */
  private isRuleOperationInProgress(): boolean
  {
    const isInProgress = this.isOfferRegistryProcessingActive
      || this.isProcessingRules
      || this.isSavingRules
      || this.activeRuleSaveAttemptId !== null;

    return isInProgress;
  }

  /**
   * Queues account-level processing for all account rules.
   */
  processRules(): void {
    if (!this.getProcessRulesButtonDisabled()) {
      this.queueProcessRulesForAllAccountRules(true);
    }
  }

  /**
   * Reports only reversible local Offer Rule changes for navigation protection.
   */
  hasUnsavedRuleChanges(): boolean
  {
    const hasUnsavedChanges = this.hasUnsavedRuleChangesState === true;

    return hasUnsavedChanges;
  }

  /**
   * Marks rule configuration changes as dirty for later child-component wiring.
   */
  handleRuleConfigurationChanged(): void {
    this.isRuleConfigurationDirty = true;
  }

  /**
   * Tracks reversible local rule changes without activating legacy navigation processing.
   */
  handleUnsavedRuleChangesChange(hasUnsavedRuleChanges: boolean): void {
    this.hasUnsavedRuleChangesState = hasUnsavedRuleChanges;
  }

  /**
   * Starts guarded bulk persistence for the current local rule change set.
   */
  saveRules(changeSet: OfferRuleChangeSet): void {
    if (this.canStartRuleSave(changeSet)) {
      const request = this.buildBulkRuleRequest(changeSet);
      const saveAttemptId = this.startRuleSaveAttempt();
      this.preflightRuleSave(request, saveAttemptId);
    }
  }

  /**
   * Checks whether the current change set can begin a bulk save attempt.
   */
  private canStartRuleSave(changeSet: OfferRuleChangeSet): boolean
  {
    const operationCount = changeSet.creates.length + changeSet.updates.length + changeSet.deletes.length;
    const canSave = operationCount > 0
      && operationCount <= maximumBulkOperations
      && this.hasUnsavedRuleChanges()
      && !this.getRuleActionsDisabled();

    return canSave;
  }

  /**
   * Converts the local change set into the bulk persistence request.
   */
  private buildBulkRuleRequest(changeSet: OfferRuleChangeSet): BulkOfferRuleRequest {
    const request: BulkOfferRuleRequest = {
      accountId: this.accountId,
      creates: changeSet.creates.map((create) => ({
        clientKey: create.clientKey,
        rule: this.cloneBulkRuleData(create.rule)
      })),
      updates: changeSet.updates.map((update) => ({
        id: update.id,
        modified: update.modified,
        rule: this.cloneBulkRuleData(update.rule)
      })),
      deletes: changeSet.deletes.map((deleteOperation) => ({
        id: deleteOperation.id,
        modified: deleteOperation.modified
      }))
    };

    return request;
  }

  /**
   * Copies writable rule values into an isolated bulk request object.
   */
  private cloneBulkRuleData(rule: BulkOfferRuleData): BulkOfferRuleData {
    const clonedRule: BulkOfferRuleData = {
      offerSetId: rule.offerSetId,
      year: rule.year,
      make: rule.make,
      model: rule.model,
      trim: rule.trim,
      maxSalePrice: rule.maxSalePrice,
      vehicleLimit: rule.vehicleLimit,
      vehicleCondition: rule.vehicleCondition,
      dollarAmountDiscount: rule.dollarAmountDiscount,
      status: rule.status,
      isPrivate: rule.isPrivate
    };

    return clonedRule;
  }

  /**
   * Marks a uniquely identified rule save attempt as active.
   */
  private startRuleSaveAttempt(): number {
    this.ruleSaveAttemptSequence += 1;
    this.activeRuleSaveAttemptId = this.ruleSaveAttemptSequence;
    this.isSavingRules = true;

    return this.ruleSaveAttemptSequence;
  }

  /**
   * Verifies process status before submitting a bulk rule save.
   */
  private preflightRuleSave(request: BulkOfferRuleRequest, saveAttemptId: number): void {
    this.offerRegistryProcessorSyncService.getProcessStatus(request.accountId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status) => {
          this.handleRuleSavePreflightStatus(status, request, saveAttemptId);
        },
        error: () => {
          this.handleRuleSavePreflightError(request.accountId, saveAttemptId);
        }
      });
  }

  /**
   * Continues a current save only when the account is not processing.
   */
  private handleRuleSavePreflightStatus(
    status: OfferRegistryProcessStatusInterface,
    request: BulkOfferRuleRequest,
    saveAttemptId: number
  ): void {
    if (this.isCurrentRuleSaveAttempt(request.accountId, saveAttemptId)) {
      const isExpectedAccount = status.accountId === request.accountId;
      if (isExpectedAccount) {
        this.handleProcessStatusSuccess(status);
        if (status.incomplete === true) {
          this.completeRuleSaveAttempt(saveAttemptId);
        }
        if (status.incomplete !== true) {
          this.submitBulkRuleSave(request, saveAttemptId);
        }
      }
      if (!isExpectedAccount) {
        this.handleProcessStatusError();
        this.completeRuleSaveAttempt(saveAttemptId);
      }
    }
  }

  /**
   * Locks rule actions and ends the save attempt when preflight status fails.
   */
  private handleRuleSavePreflightError(accountId: number, saveAttemptId: number): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      this.handleProcessStatusError();
      this.completeRuleSaveAttempt(saveAttemptId);
    }
  }

  /**
   * Sends the validated change set to the bulk persistence endpoint.
   */
  private submitBulkRuleSave(request: BulkOfferRuleRequest, saveAttemptId: number): void {
    this.offerRuleService.bulkSaveOfferRules(request)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (response) => {
          this.handleBulkRuleSaveSuccess(response, request.accountId, saveAttemptId);
        },
        error: (error: unknown) => {
          this.handleBulkRuleSaveError(error, request.accountId, saveAttemptId);
        }
      });
  }

  /**
   * Replaces local state with authoritative rules before queueing processing.
   */
  private handleBulkRuleSaveSuccess(
    response: BulkOfferRuleResponse,
    accountId: number,
    saveAttemptId: number
  ): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      const validResponseAccount = response.accountId === accountId;
      let baselineWasReplaced = false;
      if (validResponseAccount && this.offerSetRulesComponent) {
        baselineWasReplaced = this.offerSetRulesComponent.replaceWithAuthoritativeRules(response.rules);
      }
      if (baselineWasReplaced) {
        this.isSavingRules = false;
        this.queueProcessingAfterRuleSave(accountId, saveAttemptId);
      }
      if (!baselineWasReplaced) {
        this.showRuleSaveError();
        this.completeRuleSaveAttempt(saveAttemptId);
      }
    }
  }

  /**
   * Reports bulk save failures and refreshes status after processing conflicts.
   */
  private handleBulkRuleSaveError(error: unknown, accountId: number, saveAttemptId: number): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      this.isSavingRules = false;
      this.showRuleSaveError();
      if (this.isDuplicateQueueResponse(error)) {
        this.refreshStatusAfterBulkConflict(accountId, saveAttemptId);
      }
      if (!this.isDuplicateQueueResponse(error)) {
        this.completeRuleSaveAttempt(saveAttemptId);
      }
    }
  }

  /**
   * Reloads process status after a bulk save conflict.
   */
  private refreshStatusAfterBulkConflict(accountId: number, saveAttemptId: number): void {
    this.offerRegistryProcessorSyncService.getProcessStatus(accountId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status) => {
          this.handleBulkConflictStatus(status, accountId, saveAttemptId);
        },
        error: () => {
          this.handleBulkConflictStatusError(accountId, saveAttemptId);
        }
      });
  }

  /**
   * Applies refreshed conflict status and completes the current save attempt.
   */
  private handleBulkConflictStatus(
    status: OfferRegistryProcessStatusInterface,
    accountId: number,
    saveAttemptId: number
  ): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      if (status.accountId === accountId) {
        this.handleProcessStatusSuccess(status);
      }
      if (status.accountId !== accountId) {
        this.handleProcessStatusError();
      }
      this.completeRuleSaveAttempt(saveAttemptId);
    }
  }

  /**
   * Locks rule actions when conflict status cannot be refreshed.
   */
  private handleBulkConflictStatusError(accountId: number, saveAttemptId: number): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      this.handleProcessStatusError();
      this.completeRuleSaveAttempt(saveAttemptId);
    }
  }

  /**
   * Shows the generic bulk rule save failure message.
   */
  private showRuleSaveError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: ruleSaveErrorDetail
    });
  }

  /**
   * Queues account rule processing after successful bulk persistence.
   */
  private queueProcessingAfterRuleSave(accountId: number, saveAttemptId: number): void {
    this.isProcessingRules = true;
    this.offerRegistryProcessorSyncService.queueProcessForAllAccountRules(accountId)
      .pipe(
        takeUntil(this.destroySubject),
        finalize(() => {
          if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
            this.isProcessingRules = false;
          }
        })
      )
      .subscribe({
        next: () => {
          this.handleRuleSaveQueueSuccess(accountId, saveAttemptId);
        },
        error: (error: unknown) => {
          this.handleRuleSaveQueueError(error, accountId, saveAttemptId);
        }
      });
  }

  /**
   * Marks rules clean and begins status verification after queue success.
   */
  private handleRuleSaveQueueSuccess(accountId: number, saveAttemptId: number): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      this.markRulesProcessedClean();
      this.showRulesProcessingToast();
      this.isOfferRegistryProcessingActive = true;
      this.refreshStatusAfterRuleSaveQueue(accountId, saveAttemptId, false, false);
    }
  }

  /**
   * Reconciles queue failures against current process status.
   */
  private handleRuleSaveQueueError(error: unknown, accountId: number, saveAttemptId: number): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      const isDuplicateResponse = this.isDuplicateQueueResponse(error);
      if (!isDuplicateResponse) {
        this.showSavedButNotQueuedMessage();
      }
      this.refreshStatusAfterRuleSaveQueue(accountId, saveAttemptId, isDuplicateResponse, isDuplicateResponse);
    }
  }

  /**
   * Reloads process status after a rule-save queue response.
   */
  private refreshStatusAfterRuleSaveQueue(
    accountId: number,
    saveAttemptId: number,
    showPartialIfInactive: boolean,
    showSuccessIfActive: boolean
  ): void {
    this.offerRegistryProcessorSyncService.getProcessStatus(accountId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status) => {
          this.handleRuleSaveQueueStatus(
            status,
            accountId,
            saveAttemptId,
            showPartialIfInactive,
            showSuccessIfActive
          );
        },
        error: () => {
          this.handleRuleSaveQueueStatusError(accountId, saveAttemptId, showPartialIfInactive);
        }
      });
  }

  /**
   * Applies post-queue status and selects the appropriate user message.
   */
  private handleRuleSaveQueueStatus(
    status: OfferRegistryProcessStatusInterface,
    accountId: number,
    saveAttemptId: number,
    showPartialIfInactive: boolean,
    showSuccessIfActive: boolean
  ): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      const isExpectedAccount = status.accountId === accountId;
      if (isExpectedAccount) {
        this.handleProcessStatusSuccess(status);
        if (status.incomplete === true && showSuccessIfActive) {
          this.showRulesProcessingToast();
        }
        if (status.incomplete !== true && showPartialIfInactive) {
          this.showSavedButNotQueuedMessage();
        }
      }
      if (!isExpectedAccount) {
        this.handleProcessStatusError();
        if (showPartialIfInactive) {
          this.showSavedButNotQueuedMessage();
        }
      }
      this.completeRuleSaveAttempt(saveAttemptId);
    }
  }

  /**
   * Handles an unavailable post-queue status response.
   */
  private handleRuleSaveQueueStatusError(
    accountId: number,
    saveAttemptId: number,
    showPartialMessage: boolean
  ): void {
    if (this.isCurrentRuleSaveAttempt(accountId, saveAttemptId)) {
      this.handleProcessStatusError();
      if (showPartialMessage) {
        this.showSavedButNotQueuedMessage();
      }
      this.completeRuleSaveAttempt(saveAttemptId);
    }
  }

  /**
   * Warns that rules persisted but processing was not confirmed.
   */
  private showSavedButNotQueuedMessage(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Rules saved',
      detail: savedButNotQueuedDetail
    });
  }

  /**
   * Verifies that an asynchronous result belongs to the active account save.
   */
  private isCurrentRuleSaveAttempt(accountId: number, saveAttemptId: number): boolean {
    const isCurrent = !this.isComponentDestroyed
      && this.activeRuleSaveAttemptId === saveAttemptId
      && this.accountId === accountId;

    return isCurrent;
  }

  /**
   * Clears an active rule save when its identifier still matches.
   */
  private completeRuleSaveAttempt(saveAttemptId: number): void {
    if (this.activeRuleSaveAttemptId === saveAttemptId) {
      this.activeRuleSaveAttemptId = null;
      this.isSavingRules = false;
    }
  }

  /**
   * Invalidates account-scoped save, handoff, and process status state.
   */
  private prepareForRuleSaveAccountChange(): void {
    this.invalidateRuleSaveAttempt();
    this.pendingGeneratedRuleDraft = null;
    this.pendingRuleDraftToken = null;
    this.generatedRuleHandoffAttemptKey = null;
    this.isProcessStatusInitialized = false;
    this.isProcessStatusLoadFailed = false;
    this.isOfferRegistryProcessingActive = true;
    this.cancelProcessStatusPolling();
  }

  /**
   * Prevents pending save callbacks from mutating current account state.
   */
  private invalidateRuleSaveAttempt(): void {
    if (this.activeRuleSaveAttemptId !== null) {
      this.isProcessingRules = false;
    }
    this.ruleSaveAttemptSequence += 1;
    this.activeRuleSaveAttemptId = null;
    this.isSavingRules = false;
  }

  /**
   * Save IPOP configuration
   */
  saveSettings(): void
  {
    let payload: IpopConfiguration | null = null;

    if (this.ipopConfiguration && !this.getSaveButtonDisabled()) {
      this.isSaving = true;
      payload = this.buildSaveSettingsPayload(this.ipopConfiguration);
    }

    if (payload) {
      this.ipopConfigurationService.updateSettings(payload)
        .pipe(switchMap(() => this.ipopConfigurationService.getSettings(payload.accountId)), takeUntil(this.settingsLoadCancellationSubject), takeUntil(this.destroySubject)).subscribe({
        next: (settings) => {
          this.applySettingsResponse(settings);
          this.handleSaveSuccess();
        },
        error: () => {
          this.handleSaveError();
        },
        complete: () => {
          this.handleSaveComplete();
        }
      });
    }
  }

  /**
   * Build the full IPOP configuration payload from the current forms.
   */
  private buildSaveSettingsPayload(ipopConfiguration: IpopConfiguration): IpopConfiguration {
    const includePrivateOffersInExternalAdvertising = this.resolveBooleanControlValue(
      this.privateOffersForm,
      'includePrivateOffersInExternalAdvertising',
      false
    );
    const unlockPrivateOffersEnabled = this.resolveBooleanControlValue(this.privateOffersForm, 'unlockPrivateOffersEnabled', false);
    const displaySavingsMessage = this.resolveBooleanControlValue(this.privateOffersForm, 'displaySavingsMessage', true);
    const savingsMessage = this.resolveStringControlValue(this.privateOffersForm, 'savingsMessage', 'Additional Savings Unlocked');
    const savingsMessageBackgroundColor = this.resolveStringControlValue(this.privateOffersForm, 'savingsMessageBackgroundColor', '#DDE9E2');
    const showPriceComparison = this.resolveBooleanControlValue(this.privateOffersForm, 'showPriceComparison', false);
    const displayUnlockedSellingPrice = this.resolveBooleanControlValue(this.privateOffersForm, 'displayUnlockedSellingPrice', false);
    const preUnlockCtaText = this.resolveStringControlValue(this.privateOffersForm, 'preUnlockCtaText', 'Unlock Your Special Offer');
    const postUnlockCtaText = this.resolveStringControlValue(this.privateOffersForm, 'postUnlockCtaText', 'Claim Your Special Offer');
    const privateOfferTextColor = this.resolveStringControlValue(this.privateOffersForm, 'privateOfferTextColor', '#000000');
    const privateOfferButtonColor = this.resolveStringControlValue(this.privateOffersForm, 'privateOfferButtonColor', '#16A34A');

    const vdpOfferWidgetEnabled = this.resolveBooleanControlValue(this.vdpOffersForm, 'vdpOfferWidgetEnabled', false);
    const offerTextColor = this.resolveStringControlValue(this.vdpOffersForm, 'offerTextColor', '#000000');
    const ctaTextFontSize = this.resolveNumericControlValue(this.vdpOffersForm, 'ctaTextFontSize', 22);
    const ctaButtonColor = this.resolveStringControlValue(this.vdpOffersForm, 'ctaButtonColor', '#000000');
    const clickToCallEnabled = this.resolveBooleanControlValue(this.vdpOffersForm, 'clickToCallEnabled', true);
    const clickToCallText = this.resolveStringControlValue(this.vdpOffersForm, 'clickToCallText', '[DealerPhone]');
    const clickToCallTextFontSize = this.resolveNumericControlValue(this.vdpOffersForm, 'clickToCallTextFontSize', 22);
    const clickToCallBackgroundColor = this.resolveStringControlValue(this.vdpOffersForm, 'clickToCallBackgroundColor', '#000000');
    const clickToCallTextColor = this.resolveStringControlValue(this.vdpOffersForm, 'clickToCallTextColor', '#ffffff');
    const showCtaWithoutOffers = this.resolveBooleanControlValue(this.vdpOffersForm, 'showCtaWithoutOffers', false);
    let privateOfferDisclosure = '';
    if (this.privateOffersForm) {
      const value = this.privateOffersForm.get('privateOfferDisclosure')?.value;
      if (typeof value === 'string') {
        privateOfferDisclosure = value.trim();
      }
    }

    return {
      ...siteWideUnlockPayload(this.siteWideUnlockForm),
      id: ipopConfiguration.id,
      accountId: this.accountId,
      includePrivateOffersInExternalAdvertising: includePrivateOffersInExternalAdvertising,
      unlockPrivateOffersEnabled: unlockPrivateOffersEnabled,
      vdpOfferWidgetEnabled: vdpOfferWidgetEnabled,
      displaySavingsMessage: displaySavingsMessage,
      savingsMessage: savingsMessage,
      savingsMessageBackgroundColor: savingsMessageBackgroundColor,
      offerTextColor: offerTextColor,
      privateOfferTextColor: privateOfferTextColor,
      privateOfferButtonColor: privateOfferButtonColor,
      showPriceComparison: showPriceComparison,
      displayUnlockedSellingPrice: displayUnlockedSellingPrice,
      preUnlockCtaText: preUnlockCtaText,
      postUnlockCtaText: postUnlockCtaText,
      ctaTextFontSize: ctaTextFontSize,
      ctaButtonColor: ctaButtonColor,
      privateOfferDisclosure: privateOfferDisclosure,
      clickToCallEnabled: clickToCallEnabled,
      clickToCallText: clickToCallText,
      clickToCallTextFontSize: clickToCallTextFontSize,
      clickToCallBackgroundColor: clickToCallBackgroundColor,
      clickToCallTextColor: clickToCallTextColor,
      showCtaWithoutOffers: showCtaWithoutOffers,
    };
  }

  /**
   * Resolve a boolean form control value with a default when the form/control is missing.
   */
  private resolveBooleanControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: boolean): boolean {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (value === true) {
        result = true;
      }
      if (value === false) {
        result = false;
      }
    }
    return result;
  }

  /**
   * Resolve a trimmed string form control value with a default when missing/blank.
   */
  private resolveStringControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: string): string {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '') {
          result = trimmed;
        }
      }
    }
    return result;
  }

  /**
   * Resolve a number form control value with a default when missing/blank.
   */
  private resolveNumericControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: number): number {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (typeof value === 'number') {
       result = value;
      }
    }
    return result;
  }

  /**
   * Handle successful saves.
   */
  private handleSaveSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Settings updated'
    });
    this.markFormsPristine();
  }

  /**
   * Handle failed saves.
   */
  private handleSaveError(): void
  {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Unable to confirm saved settings. Your edits are retained; reload to verify before retrying.'
    });
    this.isSaving = false;
  }

  /**
   * Handle save completion.
   */
  private handleSaveComplete(): void {
    this.isSaving = false;
  }

  /**
   * Mark forms pristine after persisting settings.
   */
  private markFormsPristine(): void {
    this.siteWideUnlockForm?.markAsPristine();
    if (this.privateOffersForm) {
      this.privateOffersForm.markAsPristine();
    }
    if (this.vdpOffersForm) {
      this.vdpOffersForm.markAsPristine();
    }
  }

  /**
   * Loads account-level registry processing status.
   */
  private loadProcessStatus(): void {
    if (this.isValidAccountId()) {
      this.offerRegistryProcessorSyncService.getProcessStatus(this.accountId)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (status) => {
            this.handleProcessStatusSuccess(status);
          },
          error: () => {
            this.handleProcessStatusError();
          }
        });
    }
    if (!this.isValidAccountId()) {
      this.handleInvalidAccountId();
    }
  }

  /**
   * Checks whether the selected route account ID can be used for status and queue calls.
   */
  private isValidAccountId(): boolean {
    const isValidAccountId = Number.isInteger(this.accountId) && this.accountId > 0;

    return isValidAccountId;
  }

  /**
   * Handles invalid route account IDs without making backend calls.
   */
  private handleInvalidAccountId(): void {
    this.isProcessStatusInitialized = false;
    this.isProcessStatusLoadFailed = true;
    this.isOfferRegistryProcessingActive = true;
    this.cancelProcessStatusPolling();
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: processStatusLoadErrorDetail
    });
  }

  /**
   * Applies successful process status responses.
   */
  private handleProcessStatusSuccess(status: OfferRegistryProcessStatusInterface): void {
    this.processStatus = status;
    this.isProcessStatusInitialized = true;
    this.isProcessStatusLoadFailed = false;
    this.isOfferRegistryProcessingActive = status.incomplete === true;

    if (this.isOfferRegistryProcessingActive) {
      this.startProcessStatusPolling();
    }
    if (!this.isOfferRegistryProcessingActive) {
      this.cancelProcessStatusPolling();
    }
  }

  /**
   * Handles process status load failures.
   */
  private handleProcessStatusError(): void {
    this.isProcessStatusInitialized = false;
    this.isProcessStatusLoadFailed = true;
    this.isOfferRegistryProcessingActive = true;
    this.cancelProcessStatusPolling();
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: processStatusLoadErrorDetail
    });
  }

  /**
   * Handles successful queue responses.
   */
  private handleProcessRulesSuccess(): void {
    this.markRulesProcessedClean();
    this.showRulesProcessingToast();

    if (!this.isComponentDestroyed) {
      this.loadProcessStatus();
    }
  }

  /**
   * Queues account-level processing with optional component destroy cancellation.
   */
  private queueProcessRulesForAllAccountRules(shouldCancelOnDestroy: boolean): void {
    this.isProcessingRules = true;
    let processRulesRequest = this.offerRegistryProcessorSyncService.queueProcessForAllAccountRules(this.accountId)
      .pipe(
        finalize(() => {
          this.isProcessingRules = false;
        })
      );

    if (shouldCancelOnDestroy) {
      processRulesRequest = processRulesRequest.pipe(takeUntil(this.destroySubject));
    }

    processRulesRequest.subscribe({
      next: () => {
        this.handleProcessRulesSuccess();
      },
      error: (error: unknown) => {
        this.handleProcessRulesError(error, shouldCancelOnDestroy);
      }
    });
  }

  /**
   * Handles queue errors and duplicate in-progress responses.
   */
  private handleProcessRulesError(error: unknown, shouldCancelOnDestroy: boolean): void {
    const isDuplicateQueueResponse = this.isDuplicateQueueResponse(error);

    if (isDuplicateQueueResponse) {
      this.handleDuplicateProcessQueueResponse(shouldCancelOnDestroy);
    }
    if (!isDuplicateQueueResponse) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: processQueueErrorDetail
      });
    }
  }

  /**
   * Refreshes status after a duplicate queue response.
   */
  private handleDuplicateProcessQueueResponse(shouldCancelOnDestroy: boolean): void {
    let processStatusRequest = this.offerRegistryProcessorSyncService.getProcessStatus(this.accountId);

    if (shouldCancelOnDestroy) {
      processStatusRequest = processStatusRequest.pipe(takeUntil(this.destroySubject));
    }

    processStatusRequest.subscribe({
      next: (status) => {
        if (!this.isComponentDestroyed) {
          this.handleDuplicateProcessStatusSuccess(status);
        }
      },
      error: () => {
        if (!this.isComponentDestroyed) {
          this.handleProcessStatusError();
        }
      }
    });
  }

  /**
   * Treats duplicate queue response as in-progress only when status confirms it.
   */
  private handleDuplicateProcessStatusSuccess(status: OfferRegistryProcessStatusInterface): void {
    this.handleProcessStatusSuccess(status);

    if (status.incomplete === true && status.accountId === this.accountId) {
      this.markRulesProcessedClean();
      this.showRulesProcessingToast();
    }
    if (status.incomplete !== true || status.accountId !== this.accountId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: processQueueErrorDetail
      });
    }
  }

  /**
   * Checks whether a queue error is a duplicate command response.
   */
  private isDuplicateQueueResponse(error: unknown): boolean {
    let isDuplicateQueueResponse = false;

    if (error instanceof HttpErrorResponse && error.status === duplicateQueueStatusCode) {
      isDuplicateQueueResponse = true;
    }

    return isDuplicateQueueResponse;
  }

  /**
   * Marks rule configuration as processed/clean.
   */
  private markRulesProcessedClean(): void {
    this.isRuleConfigurationDirty = false;
  }

  /**
   * Shows required queue success toast.
   */
  private showRulesProcessingToast(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: rulesProcessingToastDetail
    });
  }

  /**
   * Starts account-level process status polling while processing is active.
   */
  private startProcessStatusPolling(): void {
    this.cancelProcessStatusPolling();
    this.offerRegistryProcessorSyncService.getProcessStatus(this.accountId)
      .pipe(
        repeat({delay: processStatusPollingIntervalMs}),
        takeUntil(this.processStatusPollingSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (status) => {
          this.handleProcessPollingStatus(status);
        },
        error: () => {
          this.handleProcessStatusError();
        }
      });
  }

  /**
   * Applies process polling responses.
   */
  private handleProcessPollingStatus(status: OfferRegistryProcessStatusInterface): void {
    this.processStatus = status;
    this.isProcessStatusInitialized = true;
    this.isProcessStatusLoadFailed = false;
    this.isOfferRegistryProcessingActive = status.incomplete === true;

    if (!this.isOfferRegistryProcessingActive) {
      this.cancelProcessStatusPolling();
    }
  }

  /**
   * Stops account-level process status polling.
   */
  private cancelProcessStatusPolling(): void {
    this.processStatusPollingSubject.next();
  }

  /**
   * Activates the browser-native unload warning for unsaved rule changes.
   */
  @HostListener('window:beforeunload', ['$event'])
  confirmOnBrowserClose(event: BeforeUnloadEvent): void
  {
    if (this.hasUnsavedRuleChanges() || this.hasUnsavedSiteWideUnlockSettings()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
}
